import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import ActivePositions from '../components/ActivePositions/ActivePositions';
import FundsAndMargins from '../components/FundsAndMargins/FundsAndMargins';
import Control from '../components/Control/Control';
import StrategyConfig from '../components/StrategyConfig/StrategyConfig';
import ClientLots from '../components/ClientLots/ClientLots';
import StrategyBindings from '../components/StrategyBindings/StrategyBindings';
import StrategyHealth from '../components/StrategyHealth/StrategyHealth';
import InfraHealth from '../components/InfraHealth/InfraHealth';
import PHealth from '../components/PHealth/PHealth';
import HFTStudioPage from '../features/hft-studio/HftStudioPage';
import TickLakePage from '../features/tickLake/TickLakePage';
import Screeners from '../components/Screeners/Screeners';
import TradingHubPage from '../features/tradingHub/TradingHubPage';
import QCockpitPage from '../features/qCockpit/QCockpitPage';
import DiagnosticsPage from '../features/diagnostics/DiagnosticsPage';
// Vyasa is rendered inside QCockpitPage as a peer family next to Cobra
// (selected via the strategyFamily toggle); no standalone dashboard view.
import StrategyPerformancePage from '../features/strategyPerformance/StrategyPerformancePage';
import TradingCockpitPage from '../features/tradingCockpit/TradingCockpitPage';
import OptionChainPage    from '../features/optionChain/OptionChainPage';
import StrategyDocsPage   from '../features/docs/StrategyDocsPage';
import { Box, Container, Grid } from '@mui/material';
import GlassPaper from '../components/GlassPaper';
import { useDashboardView, DASHBOARD_VIEWS } from '../contexts/DashboardViewContext';
import { readShellEnabled } from '../features/shell/shellFlag';
import { ShellNavProvider } from '../features/shell/ShellNavContext';
import InsightsShellLayout from '../features/shell/InsightsShellLayout';
import './Dashboard.css';

const Dashboard = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const { view, setView, refreshKey } = useDashboardView();
    const shellEnabled = readShellEnabled();
    const [searchParams] = useSearchParams();
    const urlViewHydrated = useRef(false);
    const isFullWidthView = view === 'qcockpit' || view === 'diagnostics' || view === 'stratperf' || view === 'hftstudio' || view === 'docs' || view === 'ticklake';

    useEffect(() => {
        if (urlViewHydrated.current) return;
        const urlView = searchParams.get('view');
        if (urlView && DASHBOARD_VIEWS.some((v) => v.value === urlView)) {
            setView(urlView);
        }
        urlViewHydrated.current = true;
    }, [searchParams, setView]);

    useEffect(() => {
        if (!user) {
            navigate('/access');
        }
    }, [user, navigate]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    if (!user) {
        return null;
    }

    if (shellEnabled) {
        return (
            <ShellNavProvider>
                <InsightsShellLayout />
            </ShellNavProvider>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                color: 'text.primary',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Container
                maxWidth={isFullWidthView ? false : 'xl'}
                disableGutters={isFullWidthView}
                sx={{
                    py: 3,
                    px: isFullWidthView ? { xs: 2, md: 3, xl: 4 } : undefined,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}
            >
                {view === 'control' ? (
                    <Grid container spacing={3} key={`${view}-${refreshKey}`}>
                        <Grid item xs={12} md={6}>
                            <Control />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <StrategyConfig />
                        </Grid>
                        <Grid item xs={12}>
                            <ClientLots />
                        </Grid>
                        <Grid item xs={12}>
                            <StrategyBindings />
                        </Grid>
                    </Grid>
                ) : isFullWidthView ? (
                    <Box
                        key={`${view}-${refreshKey}`}
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                        }}
                    >
                        {view === 'positions' && <ActivePositions />}
                        {view === 'fundsmargins' && <FundsAndMargins />}
                        {view === 'hftstudio' && <HFTStudioPage />}
                        {view === 'strategyhealth' && <StrategyHealth />}
                        {view === 'screeners' && <Screeners />}
                        {view === 'infrahealth' && <InfraHealth />}
                        {view === 'phealth' && <PHealth />}
                        {view === 'tradinghub' && <TradingHubPage />}
                        {view === 'qcockpit' && <QCockpitPage />}
                        {view === 'diagnostics' && <DiagnosticsPage />}
                        {view === 'ticklake' && <TickLakePage />}
                        {view === 'stratperf' && <StrategyPerformancePage />}
                        {view === 'tradingcockpit' && <TradingCockpitPage />}
                        {view === 'optionchain'   && <OptionChainPage />}
                        {view === 'docs'          && <StrategyDocsPage />}
                    </Box>
                ) : (
                    <GlassPaper
                        key={`${view}-${refreshKey}`}
                        sx={{
                            flexGrow: 1,
                            p: { xs: 2, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {view === 'positions' && <ActivePositions />}
                        {view === 'fundsmargins' && <FundsAndMargins />}
                        {view === 'hftstudio' && <HFTStudioPage />}
                        {view === 'strategyhealth' && <StrategyHealth />}
                        {view === 'screeners' && <Screeners />}
                        {view === 'infrahealth' && <InfraHealth />}
                        {view === 'phealth' && <PHealth />}
                        {view === 'tradinghub' && <TradingHubPage />}
                        {view === 'qcockpit' && <QCockpitPage />}
                        {view === 'stratperf' && <StrategyPerformancePage />}
                        {view === 'tradingcockpit' && <TradingCockpitPage />}
                        {view === 'optionchain'   && <OptionChainPage />}
                    </GlassPaper>
                )}
            </Container>
        </Box>
    );
};

export default Dashboard;
